import React from 'react';
import './TravelPage.css';
import transportImage from '../assets/travel/smart-ticketing.png';

// Key facts about the Queensland Government 50c fare initiative (source: Translink)
const fareHighlights = [
  'A flat 50c fare applies across all zones and modes on the Translink network.',
  'Covers train, bus, ferry, tram and on-demand services throughout South East Queensland.',
  'Pay with a go card, contactless credit/debit card, or paper ticket.',
  'Transfers are included — connecting bus and train trips count as one 50c journey.',
  'Excludes Airtrain services, which keep their own separate pricing.'
];

// Smart Ticketing details (source: Translink news article, 24 March 2025)
const smartTicketingHighlights = [
  'Tap on and tap off with a Visa or Mastercard credit/debit card — or a card added to your phone, smart watch or wearable device.',
  'No need to buy a ticket in advance; the traditional go card still works everywhere if you prefer.',
  'Available across all train, tram, ferry and bus services in South East Queensland.',
  'However you choose to pay, the same flat 50c fare applies.'
];

// Embedded YouTube videos for each destination

const destinations = [
  {
    name: 'Brisbane',
    website: 'https://www.queensland.com/au/en/places-to-see/destinations/brisbane',
    videoId: '0Mv48ZM7gu4',
    description: [
      'Capital city of Queensland and host city of ICES2026.',
      'Enjoy the vibrant South Bank precinct, river cruises, world-class dining, and easy access to wildlife sanctuaries.',
      'Getting here: 20-minute taxi or Airtrain ride from Brisbane Airport (BNE) to the CBD/South Bank.'
    ]
  },
  {
    name: 'Gold Coast',
    website: 'https://www.queensland.com/au/en/places-to-see/destinations/gold-coast',
    videoId: 'HpwtMAlZzEU',
    description: [
      'Famous for 70 km of pristine beaches, surfing, and vibrant nightlife at Surfers Paradise.',
      'Theme parks such as Dreamworld, Movie World, and Sea World are perfect for families.',
      'Travel time: ~1-hour drive or train from Brisbane CBD.'
    ]
  },
  {
    name: 'Sunshine Coast',
    website: 'https://www.queensland.com/au/en/places-to-see/destinations/sunshine-coast',
    videoId: 'UdJJLpx169M',
    description: [
      'Relaxed coastal region north of Brisbane featuring Noosa Heads, Australia Zoo, and Glass House Mountains.',
      'Ideal for hiking, markets, and laid-back beach culture.',
      'Travel time: ~1.5-hour drive from Brisbane CBD.'
    ]
  }
];

const TravelPage = () => (
  <section className="travel-page fade-in-up">
    <div className="container-small">
      <h1 className="travel-heading">Explore South-East Queensland</h1>
      <p className="travel-intro">Make the most of your trip to ICES2026 by discovering Brisbane and the stunning coastal regions nearby.</p>

      <div className="fares-highlight">
        <div className="fares-banner-wrapper">
          <img src={transportImage} alt="Transport for Brisbane buses in the South East Queensland public transport network" className="fares-banner" />
        </div>
        <div className="fares-content">
          <span className="fares-badge">Getting around</span>
          <h2 className="fares-title">50c Public Transport Fares in South East Queensland</h2>
          <p className="fares-text">
            Getting around Brisbane and the wider region has never been more affordable. Under a Queensland
            Government initiative delivered through Translink, most public transport journeys cost a flat 50 cents.
          </p>
          <ul className="fares-list">
            {fareHighlights.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>

          <hr className="fares-divider" />

          <h3 className="fares-subtitle">Smart Ticketing</h3>
          <p className="fares-text">
            Across all of South East Queensland you can now pay for travel with Smart Ticketing — simply
            tap on and tap off, no queuing for a paper ticket required.
          </p>
          <ul className="fares-list">
            {smartTicketingHighlights.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>

          <div className="fares-actions">
            <a
              href="https://translink.com.au/tickets-and-fares/50-cent-fares"
              target="_blank"
              rel="noreferrer"
              className="fares-link"
            >
              Learn more about 50c fares →
            </a>
            <a
              href="https://translink.com.au/news-and-media/articles/719331"
              target="_blank"
              rel="noreferrer"
              className="fares-link fares-link-secondary"
            >
              About Smart Ticketing →
            </a>
          </div>
        </div>
      </div>

      {destinations.map((dest, idx) => (
        <a key={dest.name} href={dest.website} target="_blank" rel="noreferrer" className={`destination ${idx % 2 !== 0 ? 'reverse' : ''}`}>
          <div className="dest-video-wrapper">
            <iframe
              src={`https://www.youtube.com/embed/${dest.videoId}?rel=0`}
              title={dest.name}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              loading="lazy"
            ></iframe>
          </div>
          <div className="dest-content">
            <h2 className="dest-title">{dest.name}</h2>
            <ul className="dest-description">
              {dest.description.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </div>
        </a>
      ))}
    </div>
  </section>
);

export default TravelPage;
